<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :active="(row: Permission, id: number) => active(row, id)"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :disabled-rows="disabledRows"
    :editable="false"
    :removable="false"
    @selected="(value: Permission[]) => (selectedRows = value)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="(row: Permission) => emit('clickRow', row)"
  >
    <template #actions>
      <InfuseButton
        v-if="showGrantPermission"
        v-permission="{ id: grantPermissionPermissionId, check: 'all', hide: false }"
        class="flex w-64 items-center"
        size="medium"
        color="primary"
        @click="emit('grant-permission')"
      >
        {{ t('iam.permissions.grant_permissions.header') }}
      </InfuseButton>
    </template>

    <template #permissionId="{ data, row }">
      <div class="flex flex-col">
        <span>{{ row.subscriptionName }}</span>
        <span class="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap font-normal italic text-neutral-500">
          {{ data }}
          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(`${data}`)"
            />
          </InfuseCopy>
        </span>
      </div>
    </template>

    <template #name="{ data, row }">
      <div class="flex flex-col">
        <span>{{ data }}</span>
        <span
          class="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap font-normal italic text-neutral-500"
          :title="row.description"
        >
          {{ row.description }}
        </span>
      </div>
    </template>

    <template #category="{ data, row }">
      <div class="flex flex-col">
        <span>{{ data }}</span>
        <span class="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap font-normal italic text-neutral-500">
          {{ row.objectType }}
        </span>
      </div>
    </template>

    <template #accessType="{ data, row }">
      <InfuseTag inline>
        {{ t(`iam.permissions.accessType.value.${data}`) }}
      </InfuseTag>

      <InfuseTag v-if="row.bulkAction" variant="warning" class="ml-2" inline>
        {{ t('iam.permissions.bulkAction') }}
      </InfuseTag>
    </template>
  </InfuseTable>

  <slot name="grant-permission-modal" />
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { Permission } from '@infuse/iam/permission/domain';
import { vPermission } from '@infuse/iam/permission/directives';
import { InfuseButton, InfuseCopy, InfuseTable, InfuseTag } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Emits {
  (e: 'load'): void;
  (e: 'clickRow', row: Permission): void;
  (e: 'search', query: string): void;
  (e: 'grant-permission'): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<Permission[]>([]);

interface Props {
  rows: Permission[];
  active?: (row: Permission, id?: number) => boolean;
  loading: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  hasMoreItems: boolean;
  showGrantPermission?: boolean;
  grantPermissionPermissionId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  active: () => false,
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  hasMoreItems: true,
  showGrantPermission: false,
  grantPermissionPermissionId: ''
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.permissions.searchPlaceholder'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => {
  const row = selectedRows.value[0];
  if (!row) {
    return [];
  }
  return props.rows.filter((perm) => perm.id !== row.id);
});

const columns: UiTableColumn[] = [
  {
    name: 'permissionId',
    label: () => t('iam.permissions.permissionId'),
    field: 'permissionId',
    bold: true
  },
  {
    name: 'name',
    label: () => t('iam.permissions.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'category',
    label: () => t('iam.permissions.category'),
    field: 'category.name',
    bold: true
  },
  {
    name: 'accessType',
    label: () => t('iam.permissions.accessType.label'),
    field: 'accessType'
  }
];
</script>
